// Small presentational cells shared by the Sources table and drawer: the
// real-series sparkline, the orbit pill, and the truncating pill lists.

import React from "react";
import type { Orbit } from "@/types/vb";
import { ORBIT_META } from "@/components/vb/OrbitBar";
import { providerMeta } from "@/pages/vb/run/providerMeta";
import { sparkPath } from "./sourcesUtil";

export function SparkLine({ series, w, h }: { series: number[]; w: number; h: number }) {
  // A line needs at least two points; a single month would draw a lone dot that
  // reads as a stray mark. Show nothing until there's real trend to plot.
  if (!series || series.length < 2) return null;
  const { d, cx, cy } = sparkPath(series, w, h);
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" className="src-spark">
      <path d={d} fill="none" stroke="var(--vb-accent-teal)" strokeWidth={1.5} />
      <circle cx={cx} cy={cy} r={2} fill="var(--vb-accent-teal)" />
    </svg>
  );
}

export function OrbitPill({ orbit }: { orbit: Orbit | null }) {
  if (!orbit) {
    return <span className="src-pill-orbit src-pill-orbit--none">Unclassified</span>;
  }
  return (
    <span className="src-pill-orbit">
      <span className={`src-orbit-dot src-orbit-dot--${orbit}`} aria-hidden="true" />
      {ORBIT_META[orbit].tech}
    </span>
  );
}

/** Up to `max` signal pills + a "+N" overflow. Each pill truncates (ellipsis)
 *  in the narrow table cell; the full name is in the title tooltip. */
export function SignalPills({ signals, max }: { signals: string[]; max: number }) {
  const shown = signals.slice(0, max);
  const extra = signals.length - shown.length;
  return (
    <span className="src-cell-wrap">
      {shown.map((s) => (
        <span className="src-pill-signal" key={s} title={s}>
          {s}
        </span>
      ))}
      {extra > 0 && <span className="src-cell-more">+{extra}</span>}
    </span>
  );
}

/** AI-model pills: plain text on desktop; on mobile the brand icon mark only
 *  (CSS swaps which is shown), reusing the run-screen's `providerMeta` logos. */
export function ModelPills({ models, max }: { models: string[]; max: number }) {
  const shown = models.slice(0, max);
  const extra = models.length - shown.length;
  return (
    <span className="src-cell-wrap">
      {shown.map((m) => {
        const meta = providerMeta(m);
        return (
          <span className="src-pill-model" key={m}>
            <span className={`src-model-ico ${meta.className}`} aria-hidden="true">
              {meta.logo}
            </span>
            <span className="src-model-name">{m}</span>
          </span>
        );
      })}
      {extra > 0 && <span className="src-cell-more">+{extra}</span>}
    </span>
  );
}
